/**
 * useVoiceEntitlement — MAY THIS LEARNER USE THEIR VOICE? The client half of
 * api/_utils/voiceEntitlement.ts (job #490).
 *
 * Tom's ruling, 2026-09-21: text in study groups is free to every adult on
 * the course, at any belt since jobs #119/#122; VOICE — voice notes, huddles, the two-minute calls —
 * is ONE per-learner entitlement at £5 a month, included in the £15 plan,
 * shown only at the padlock. This composable is the one client read of that
 * flag. The call screen, the groups screens and anything else with a voice
 * button call `ensure()` before the action and mount <VoicePadlock> when it
 * answers false. Nothing else decides, and nothing shows a price anywhere but
 * the padlock.
 *
 * WHERE IT READS FROM: `voice` on GET /api/entitlement/user — the endpoint the
 * app already reads for entitlements, so one round trip carries both. The
 * server resolves it from the entitlement rows and the subscription spine;
 * the client never derives it from anything local.
 *
 * SHARED, MODULE-LEVEL STATE. Whichever button asked, the app holds one
 * answer; a purchase landing (usePendingPurchase polls `refresh()` for a
 * voice plan) flips every padlock at once.
 *
 * FAILS SHUT, HONESTLY. `entitled` is null until the first answer, false on a
 * failed read. A voice button that cannot learn the answer shows the padlock
 * rather than starting a relay it may not be allowed to use — the padlock's
 * own refresh is the way back.
 *
 * SIGNED-OUT: null → the padlock. Voice actions already sit behind sign-in and
 * a course enrolment (socialEligibility), so this is never the first door a
 * person meets.
 */

import { ref, computed, inject, readonly, type Ref } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'
import { resolveSupabase } from './schools/client'
import { bootFetch } from '../playback/signedInBoot'

export type VoiceVia = 'voice' | 'plan' | 'family' | null

export interface VoiceEntitlementState {
  entitled: boolean
  via: VoiceVia
  expiresAt: string | null
  price: { monthlyPence: number; currency: 'GBP'; priceId: string | null }
}

/** The wire code a voice route answers with when the £5 padlock applies. */
export const VOICE_REQUIRED_CODE = 'voice_required'
/** The wire code any route answers with when the £15 plan padlock applies (job #576). */
export const PLAN_REQUIRED_CODE = 'plan_required'

/** Which checkout a padlock sells: the £5 feature or the £15 plan. */
export type PadlockPlan = 'voice' | 'premium'

/** The reference price, used only until the server has answered. */
const DEFAULT_PRICE = { monthlyPence: 500, currency: 'GBP' as const, priceId: null }

const state = ref<VoiceEntitlementState | null>(null)
const loading = ref(false)
/** The school class this learner is a student in, when the server says so (job #689): the £5 offer's address. */
const schoolClassId = ref<string | null>(null)
/** The course codes of that pupil's school class(es): the only courses the raised pupil free limit applies to. */
const schoolCourseCodes = ref<string[]>([])
/** Read-only view of the pupil's class course codes for the seed gate (useEntitlement). */
export const pupilCourseCodesState = readonly(schoolCourseCodes)
/** Read-only view of the pupil flag for the seed gate (useEntitlement): a class-joined pupil's free wall is the end of Green. */
export const pupilClassIdState = readonly(schoolClassId)

/**
 * Take the pupil flags from an /api/entitlement/user answer, whoever asked.
 * The player's seed gate waits for the course-entitlement answer before it
 * decides where the wall is; that answer carries `schoolLearner` too, so it
 * fills these refs on the way in. Before job #115 only this composable's own
 * refresh did, and nothing called it at boot: a pupil resuming at seed 50 of
 * their class course met the end-of-Yellow wall three seconds in.
 * `null` (no session, 401) clears them.
 */
export function notePupilFromEntitlementAnswer(data: unknown): void {
  const learner = (data as { schoolLearner?: { classId?: unknown; courseCodes?: unknown } } | null)?.schoolLearner
  schoolClassId.value = typeof learner?.classId === 'string' ? learner.classId : null
  schoolCourseCodes.value = Array.isArray(learner?.courseCodes)
    ? learner.courseCodes.filter((c: unknown): c is string => typeof c === 'string')
    : []
}
let inflight: Promise<boolean> | null = null
/** Sign-out or a route refusal invalidates pending reads, including cleanup. */
let resetVersion = 0

// COMING BACK TO THE APP RE-ASKS. The answer is a fact about a subscription
// somebody may have bought, or been given, on another screen or by somebody
// else entirely — so a tab that has been in the background has no business
// trusting what it was told before it went there. Job #541 dropped the cached
// answer here; job #576 re-reads it IN PLACE instead (hookForeground below),
// so an entitled learner never sees the padlock flash while the answer is
// re-fetched, and the course grid rides the same trigger.

/** "£5 a month", formatted from pence so the padlock and the server agree. */
export function voicePriceLine(pence: number = DEFAULT_PRICE.monthlyPence): string {
  const pounds = pence / 100
  return `£${Number.isInteger(pounds) ? pounds : pounds.toFixed(2)}`
}

/**
 * Is this error body the server's padlock? A voice route that refuses with
 * 402 + code 'voice_required' is telling the client to mount VoicePadlock
 * rather than show an error.
 */
export function isVoiceRequired(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false
  const code = (body as { code?: unknown }).code
  return code === VOICE_REQUIRED_CODE || code === PLAN_REQUIRED_CODE
}

/** Which padlock a 402 body asks for. The £15 plan unless the body says £5. */
export function padlockPlanFor(body: unknown): PadlockPlan {
  const code = body && typeof body === 'object' ? (body as { code?: unknown }).code : null
  return code === VOICE_REQUIRED_CODE ? 'voice' : 'premium'
}

// ── Refresh on foreground and on 402 (job #576) ─────────────────────────────
//
// Job #503's cold verification found that an open session kept its padlock
// after a grant landed: the answer was cached and nothing re-asked. Two
// triggers now re-ask. The app returning to the foreground — a learner who
// paid in Paddle's tab, or was granted while the app sat open — and any
// route answering 402, which means the client's picture and the server's
// disagree, so the client's is thrown away and re-read. The grid composable
// registers its own refresher here so one hook serves both answers.

const refreshers = new Set<() => Promise<unknown>>()
let foregroundHooked = false

/** Another entitlement cache asks to be refreshed on the same triggers. */
export function registerEntitlementRefresher(fn: () => Promise<unknown>): void {
  refreshers.add(fn)
  hookForeground()
}

async function refreshEverything(voiceToo: boolean): Promise<void> {
  const jobs: Promise<unknown>[] = []
  if (voiceToo) jobs.push(refreshShared())
  for (const fn of refreshers) jobs.push(Promise.resolve().then(fn).catch(() => undefined))
  await Promise.all(jobs)
}

/**
 * Back in the foreground. A held answer is DROPPED first, so `ensure()` can
 * never hand back the stale "no" (job #541's guarantee), and then re-read in
 * the same breath, so the next tap waits on the fresh answer rather than
 * making its own round trip. A signed-out or never-asked session has nothing
 * stale to correct and no token to spend, so only the other caches re-read.
 */
function onForeground(): void {
  const held = !!state.value
  if (held) {
    // A request started before foregrounding cannot correct the stale cache.
    resetVersion++
    inflight = null
    loading.value = false
    state.value = null
  }
  void refreshEverything(held)
}

function hookForeground(): void {
  if (foregroundHooked || typeof document === 'undefined') return
  foregroundHooked = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onForeground()
  })
  if (typeof window !== 'undefined') window.addEventListener('focus', onForeground)
}

/**
 * A route said 402. Whatever the client believed is stale: forget it and
 * re-read, so the padlock the learner meets next is the server's current
 * answer and a grant that landed a moment ago opens the door on the next tap.
 */
export function noteEntitlementRefused(): void {
  // An answer requested before the refusal cannot correct the stale cache.
  resetVersion++
  inflight = null
  loading.value = false
  state.value = null
  void refreshEverything(true)
}

/** The shared refresh, callable outside setup (no inject needed for the token read). */
async function refreshShared(): Promise<boolean> {
  return refreshWith(null)
}

async function authTokenFrom(supabaseRef: Ref<SupabaseClient | null> | null): Promise<string | null> {
  const sb = resolveSupabase(supabaseRef)
  if (!sb) return null
  try {
    const { data } = await sb.auth.getSession()
    return data?.session?.access_token ?? null
  } catch {
    return null
  }
}

/** Re-read from the server. Resolves to the fresh answer; false on any failure. */
async function refreshWith(supabaseRef: Ref<SupabaseClient | null> | null): Promise<boolean> {
    if (inflight) return inflight
    const version = resetVersion
    inflight = (async () => {
      loading.value = true
      try {
        const token = await authTokenFrom(supabaseRef)
        if (version !== resetVersion) return false
        if (!token) {
          state.value = null
          notePupilFromEntitlementAnswer(null)
          return false
        }
        const resp = await bootFetch('/api/entitlement/user', { headers: { Authorization: `Bearer ${token}` } })
        if (version !== resetVersion) return false
        if (!resp.ok) {
          state.value = null
          if (resp.status === 401) notePupilFromEntitlementAnswer(null)
          return false
        }
        const data = await resp.json().catch(() => ({}))
        if (version !== resetVersion) return false
        notePupilFromEntitlementAnswer(data)
        const v = data?.voice
        if (!v || typeof v.entitled !== 'boolean') {
          // An older server without the field: no voice, reference price.
          state.value = { entitled: false, via: null, expiresAt: null, price: DEFAULT_PRICE }
          return false
        }
        state.value = {
          entitled: v.entitled,
          via: v.via ?? null,
          expiresAt: v.expiresAt ?? null,
          price: v.price ?? DEFAULT_PRICE,
        }
        return v.entitled
      } catch {
        if (version === resetVersion) state.value = null
        return false
      } finally {
        if (version === resetVersion) {
          loading.value = false
          inflight = null
        }
      }
    })()
    return inflight
}

export function useVoiceEntitlement() {
  const supabaseRef = inject<Ref<SupabaseClient | null> | null>('supabase', null)
  hookForeground()

  function refresh(): Promise<boolean> {
    return refreshWith(supabaseRef)
  }

  /**
   * The guard a voice button calls before acting: the known answer if there
   * is one, else one read. `await ensure()` false → mount the padlock.
   */
  async function ensure(): Promise<boolean> {
    if (state.value) return state.value.entitled
    if (inflight) return inflight
    return refresh()
  }

  /** Forget the answer, e.g. on sign-out. */
  function reset(): void {
    resetVersion++
    inflight = null
    loading.value = false
    state.value = null
    schoolClassId.value = null
    schoolCourseCodes.value = []
  }

  /**
   * Forget the answer WITHOUT asking for a new one, so the next `ensure()`
   * reads fresh. This is the way out of a stale negative: a learner granted
   * voice while their tab sat open used to stay padlocked for the life of
   * that tab, because `ensure()` only reads when it has never read (job #541,
   * refuting the guarantee job #538 cold-checked).
   */
  function invalidate(): void {
    // Pending answers belong to the cache being forgotten, too.
    resetVersion++
    inflight = null
    loading.value = false
    state.value = null
  }

  return {
    /** The school class to buy the £5 personal premium through; null when not a school learner. */
    schoolClassId: readonly(schoolClassId),
    schoolCourseCodes: readonly(schoolCourseCodes),
    /** null = not yet known / signed out; otherwise the server's answer. */
    entitled: computed<boolean | null>(() => (state.value ? state.value.entitled : null)),
    via: computed<VoiceVia>(() => state.value?.via ?? null),
    expiresAt: computed<string | null>(() => state.value?.expiresAt ?? null),
    /** The price the padlock sells; priceId null until the Paddle product exists. */
    price: computed(() => state.value?.price ?? DEFAULT_PRICE),
    /** "£5" — the line the padlock shows. */
    priceLine: computed(() => voicePriceLine((state.value?.price ?? DEFAULT_PRICE).monthlyPence)),
    loading: readonly(loading),
    refresh,
    ensure,
    invalidate,
    reset,
  }
}
